import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Filters } from '../types';
import { rentPropertyTypes, buyPropertyTypes } from '../utils';
import { haversineDistance } from '../../../lib/utils';
// Removed Google Maps imports - now using OpenStreetMap
import { isPropertyTypeMatch } from '../../../lib/utils/property-class-cache';
// Removed Google Maps API key import - now using OpenStreetMap Nominatim

interface HomeFiltersContextType {
  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  setDebouncedSearchQuery: (query: string) => void;
  isSearching: boolean;
  setIsSearching: (searching: boolean) => void;
  listings: any[];
  setListings: (listings: any[]) => void;
  searchCity: string;
  setSearchCity: (city: string) => void;
  searchState: string;
  setSearchState: (state: string) => void;
  
  // Filter state
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFilterTab: 'buy' | 'rent' | 'sold';
  setActiveFilterTab: (tab: 'buy' | 'rent' | 'sold') => void;
  handleSetActiveFilterTab: (tab: 'buy' | 'rent' | 'sold') => void;
  filters: Filters;
  setFilters: (filters: Filters | ((prev: Filters) => Filters)) => void;
  
  // Property type state
  selectedPropertyTypesBuySold: string[];
  setSelectedPropertyTypesBuySold: (types: string[]) => void;
  selectedPropertyTypesRent: string[];
  setSelectedPropertyTypesRent: (types: string[]) => void;
  propertyTypePillTouchedRent: boolean;
  setPropertyTypePillTouchedRent: (touched: boolean) => void;
  propertyTypePillTouchedBuySold: boolean;
  setPropertyTypePillTouchedBuySold: (touched: boolean) => void;
  showPropertyTypeModal: boolean;
  setShowPropertyTypeModal: (show: boolean) => void;
  propertyTypeWarning: string;
  setPropertyTypeWarning: (warning: string) => void;
  
  // Price state
  priceRangeBuySold: [number | null, number | null];
  setPriceRangeBuySold: (range: [number | null, number | null]) => void;
  priceRangeRent: [number | null, number | null];
  setPriceRangeRent: (range: [number | null, number | null]) => void;
  
  // Beds and baths state
  bedsBuySold: number | null;
  setBedsBuySold: (beds: number | null) => void;
  bedsRent: number | null;
  setBedsRent: (beds: number | null) => void;
  bathsBuySold: number | null;
  setBathsBuySold: (baths: number | null) => void;
  bathsRent: number | null;
  setBathsRent: (baths: number | null) => void;
  
  // Search area state
  buySearchAreaMiles: number;
  setBuySearchAreaMiles: (miles: number) => void;
  rentSearchAreaMiles: number;
  setRentSearchAreaMiles: (miles: number) => void;
  soldSearchAreaMiles: number;
  setSoldSearchAreaMiles: (miles: number) => void;
  showSearchAreaDropdown: boolean;
  setShowSearchAreaDropdown: (show: boolean) => void;
  pendingBuySearchAreaMiles: number | null;
  setPendingBuySearchAreaMiles: (miles: number | null) => void;
  pendingRentSearchAreaMiles: number | null;
  setPendingRentSearchAreaMiles: (miles: number | null) => void;
  pendingSoldSearchAreaMiles: number | null;
  setPendingSoldSearchAreaMiles: (miles: number | null) => void;
  searchAreaPillTouchedBuy: boolean;
  setSearchAreaPillTouchedBuy: (touched: boolean) => void;
  searchAreaPillTouchedRent: boolean;
  setSearchAreaPillTouchedRent: (touched: boolean) => void;
  searchAreaPillTouchedSold: boolean;
  setSearchAreaPillTouchedSold: (touched: boolean) => void;
  
  // Modal state
  // Amenities modal removed
  showSquareFeetModal: boolean;
  setShowSquareFeetModal: (show: boolean) => void;
  showLotSizeModal: boolean;
  setShowLotSizeModal: (show: boolean) => void;
  showLotSizeDropdown: boolean;
  setShowLotSizeDropdown: (show: boolean) => void;
  showHomeAgeDropdown: boolean;
  setShowHomeAgeDropdown: (show: boolean) => void;
  showDaysOnMarketModal: boolean;
  setShowDaysOnMarketModal: (show: boolean) => void;
  showSquareFeetDropdown: boolean;
  setShowSquareFeetDropdown: (show: boolean) => void;
  
  // Filter values
  minHomeAge: string;
  setMinHomeAge: (age: string) => void;
  maxHomeAge: string;
  setMaxHomeAge: (age: string) => void;
  minSquareFeet: string;
  setMinSquareFeet: (sqft: string) => void;
  maxSquareFeet: string;
  setMaxSquareFeet: (sqft: string) => void;
  pendingMinSquareFeet: string;
  setPendingMinSquareFeet: (sqft: string) => void;
  pendingMaxSquareFeet: string;
  setPendingMaxSquareFeet: (sqft: string) => void;
  minLotSize: string;
  setMinLotSize: (lot: string) => void;
  maxLotSize: string;
  setMaxLotSize: (lot: string) => void;
  maxHoaFees: string;
  setMaxHoaFees: (fees: string) => void;
  noHoa: boolean;
  setNoHoa: (noHoa: boolean) => void;
  pendingDaysOnMarket: number | null;
  setPendingDaysOnMarket: (days: number | null) => void;
  // Amenities state removed
  // Amenities-related properties removed
  
  // Animation state
  resetBouncing: boolean;
  setResetBouncing: (bouncing: boolean) => void;
  daysOnMarketResetBouncing: boolean;
  setDaysOnMarketResetBouncing: (bouncing: boolean) => void;
  // Amenities animation state removed
  
  // Info modal state
  infoModal: null | 'pending' | 'hoa';
  setInfoModal: (modal: null | 'pending' | 'hoa') => void;
  
  // Commute state
  commuteAddressRent: string;
  setCommuteAddressRent: (address: string) => void;
  commuteDriveTimeRent: number | null;
  setCommuteDriveTimeRent: (time: number | null) => void;
  commuteAddressTouched: boolean;
  setCommuteAddressTouched: (touched: boolean) => void;
  commuteSuggestions: any[];
  setCommuteSuggestions: (suggestions: any[]) => void;
  searchLocationCoords: { lat: number; lng: number } | null;
  setSearchLocationCoords: (coords: { lat: number; lng: number } | null) => void;
  commuteCoords: { lat: number; lng: number } | null;
  setCommuteCoords: (coords: { lat: number; lng: number } | null) => void;
  driveTimes: number[] | null;
  setDriveTimes: (times: number[] | null) => void;
  driveTimesUpdateTrigger: number;
  setDriveTimesUpdateTrigger: (trigger: number | ((prev: number) => number)) => void;
  isCalculatingDriveTimes: boolean;
  setIsCalculatingDriveTimes: (calculating: boolean) => void;
  
  // Helper functions
  getSelectedPropertyTypes: () => string[];
  setSelectedPropertyTypesForTab: (types: string[]) => void;
  getCurrentPriceRange: () => [number | null, number | null];
  setCurrentPriceRange: (range: [number | null, number | null]) => void;
  getCurrentBeds: () => number | null;
  setCurrentBeds: (val: number | null) => void;
  getCurrentBaths: () => number | null;
  setCurrentBaths: (val: number | null) => void;
  getPropertyTypePillTouched: () => boolean;
  setPropertyTypePillTouchedForTab: (value: boolean) => void;
  getCurrentSearchAreaMiles: () => number;
  setCurrentSearchAreaMiles: (value: number) => void;
  getPendingSearchAreaMiles: () => number | null;
  setPendingSearchAreaMilesForTab: (value: number | null) => void;
  getSearchAreaPillTouched: () => boolean;
  setSearchAreaPillTouchedForTab: (value: boolean) => void;
  
  // Reset functions
  resetBuySoldFilters: () => void;
  resetRentFilters: () => void;
  clearFilters: () => void;
  
  // Filter change handler
  handleFilterChange: (key: keyof Filters, value: any) => void;
  
  // Property type handlers
  togglePropertyType: (type: string) => void;
  toggleFilterOption: (key: keyof Filters) => void;
  getFilteredListingsCount: () => number;
  refreshPropertyTypes: () => void;
  // Removed Google Maps - now using OpenStreetMap
}

const HomeFiltersContext = createContext<HomeFiltersContextType | undefined>(undefined);

interface HomeFiltersProviderProps {
  children: ReactNode;
}

export function HomeFiltersProvider({ children }: HomeFiltersProviderProps) {
  // Helper function to load state from localStorage
  const loadStateFromStorage = (key: string, defaultValue: any) => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(`homeFilters_${key}`);
        return stored ? JSON.parse(stored) : defaultValue;
      } catch (error) {
        // Error loading from localStorage
        return defaultValue;
      }
    }
    return defaultValue;
  };

  // Helper function to save state to localStorage
  const saveStateToStorage = (key: string, value: any) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(`homeFilters_${key}`, JSON.stringify(value));
      } catch (error) {
        // Error saving to localStorage
      }
    }
  };

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>(() => loadStateFromStorage('searchQuery', ''));
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(() => loadStateFromStorage('debouncedSearchQuery', ''));
  const [isSearching, setIsSearching] = useState(false);
  const [listings, setListings] = useState<any[]>(() => {
    const loadedListings = loadStateFromStorage('listings', []);

    return loadedListings;
  });
  const [searchCity, setSearchCity] = useState<string>(() => loadStateFromStorage('searchCity', ''));
  const [searchState, setSearchState] = useState<string>(() => loadStateFromStorage('searchState', ''));
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState<'buy' | 'rent' | 'sold'>(() => loadStateFromStorage('activeFilterTab', 'buy'));
  
  // Custom setActiveFilterTab that triggers searching state
  const handleSetActiveFilterTab = (tab: 'buy' | 'rent' | 'sold') => {
    if (tab !== activeFilterTab) {
      setIsSearching(true);
      // Clear the last active filter tab to ensure a fresh search is triggered
      // This will be handled by the Home component's useEffect
    }
    setActiveFilterTab(tab);
  };
  
  const [filters, setFilters] = useState<Filters>(() => loadStateFromStorage('filters', {
    priceRange: [null, null],
    beds: null,
    baths: null,
    propertyType: null,
    hidePendingContingent: false,
    newConstructionOnly: false,
    hideHomesNotYetBuilt: false,
    daysOnMarket: null,
  }));
  
  // Property type state - using cached property types
  const [selectedPropertyTypesBuySold, setSelectedPropertyTypesBuySold] = useState(() => {
    const cached = loadStateFromStorage('selectedPropertyTypesBuySold', null);
    return cached || [...buyPropertyTypes];
  });
  const [selectedPropertyTypesRent, setSelectedPropertyTypesRent] = useState(() => {
    const cached = loadStateFromStorage('selectedPropertyTypesRent', null);
    return cached || [...rentPropertyTypes];
  });
  const [propertyTypePillTouchedRent, setPropertyTypePillTouchedRent] = useState(() => loadStateFromStorage('propertyTypePillTouchedRent', false));
  const [propertyTypePillTouchedBuySold, setPropertyTypePillTouchedBuySold] = useState(() => loadStateFromStorage('propertyTypePillTouchedBuySold', false));
  const [showPropertyTypeModal, setShowPropertyTypeModal] = useState(false);
  const [propertyTypeWarning, setPropertyTypeWarning] = useState('');
  
  // Price state
  const [priceRangeBuySold, setPriceRangeBuySold] = useState<[number | null, number | null]>(() => loadStateFromStorage('priceRangeBuySold', [null, null]));
  const [priceRangeRent, setPriceRangeRent] = useState<[number | null, number | null]>(() => loadStateFromStorage('priceRangeRent', [null, null]));
  
  // Beds and baths state
  const [bedsBuySold, setBedsBuySold] = useState<number | null>(() => loadStateFromStorage('bedsBuySold', null));
  const [bedsRent, setBedsRent] = useState<number | null>(() => loadStateFromStorage('bedsRent', null));
  const [bathsBuySold, setBathsBuySold] = useState<number | null>(() => loadStateFromStorage('bathsBuySold', null));
  const [bathsRent, setBathsRent] = useState<number | null>(() => loadStateFromStorage('bathsRent', null));
  
  // Search area state
  const [buySearchAreaMiles, setBuySearchAreaMiles] = useState(() => loadStateFromStorage('buySearchAreaMiles', 0));
  const [rentSearchAreaMiles, setRentSearchAreaMiles] = useState(() => loadStateFromStorage('rentSearchAreaMiles', 0));
  const [soldSearchAreaMiles, setSoldSearchAreaMiles] = useState(() => loadStateFromStorage('soldSearchAreaMiles', 0));
  const [showSearchAreaDropdown, setShowSearchAreaDropdown] = useState(false);
  const [pendingBuySearchAreaMiles, setPendingBuySearchAreaMiles] = useState<number | null>(() => loadStateFromStorage('pendingBuySearchAreaMiles', null));
  const [pendingRentSearchAreaMiles, setPendingRentSearchAreaMiles] = useState<number | null>(() => loadStateFromStorage('pendingRentSearchAreaMiles', null));
  const [pendingSoldSearchAreaMiles, setPendingSoldSearchAreaMiles] = useState<number | null>(() => loadStateFromStorage('pendingSoldSearchAreaMiles', null));
  const [searchAreaPillTouchedBuy, setSearchAreaPillTouchedBuy] = useState(() => loadStateFromStorage('searchAreaPillTouchedBuy', false));
  const [searchAreaPillTouchedRent, setSearchAreaPillTouchedRent] = useState(() => loadStateFromStorage('searchAreaPillTouchedRent', false));
  const [searchAreaPillTouchedSold, setSearchAreaPillTouchedSold] = useState(() => loadStateFromStorage('searchAreaPillTouchedSold', false));
  
  // Modal state
  const [showSquareFeetModal, setShowSquareFeetModal] = useState(false);
  const [showLotSizeModal, setShowLotSizeModal] = useState(false);
  const [showLotSizeDropdown, setShowLotSizeDropdown] = useState(false);
  const [showHomeAgeDropdown, setShowHomeAgeDropdown] = useState(false);
  const [showDaysOnMarketModal, setShowDaysOnMarketModal] = useState(false);
  const [showSquareFeetDropdown, setShowSquareFeetDropdown] = useState(false);
  
  // Filter values
  const [minHomeAge, setMinHomeAge] = useState<string>(() => loadStateFromStorage('minHomeAge', ''));
  const [maxHomeAge, setMaxHomeAge] = useState<string>(() => loadStateFromStorage('maxHomeAge', ''));
  const [minSquareFeet, setMinSquareFeet] = useState<string>(() => loadStateFromStorage('minSquareFeet', ''));
  const [maxSquareFeet, setMaxSquareFeet] = useState<string>(() => loadStateFromStorage('maxSquareFeet', ''));
  const [pendingMinSquareFeet, setPendingMinSquareFeet] = useState<string>(() => loadStateFromStorage('pendingMinSquareFeet', ''));
  const [pendingMaxSquareFeet, setPendingMaxSquareFeet] = useState<string>(() => loadStateFromStorage('pendingMaxSquareFeet', ''));
  const [minLotSize, setMinLotSize] = useState<string>(() => loadStateFromStorage('minLotSize', ''));
  const [maxLotSize, setMaxLotSize] = useState<string>(() => loadStateFromStorage('maxLotSize', ''));
  const [maxHoaFees, setMaxHoaFees] = useState<string>(() => loadStateFromStorage('maxHoaFees', ''));
  const [noHoa, setNoHoa] = useState(() => loadStateFromStorage('noHoa', false));
  const [pendingDaysOnMarket, setPendingDaysOnMarket] = useState<number | null>(() => loadStateFromStorage('pendingDaysOnMarket', null));
  // Amenities filter removed - data was unreliable

  // Amenities enhancement function removed - amenities filter was removed

  // Amenities matching function removed - amenities filter was removed
  
  // Animation state
  const [resetBouncing, setResetBouncing] = useState(false);
  const [daysOnMarketResetBouncing, setDaysOnMarketResetBouncing] = useState(false);
  
  // Info modal state
  const [infoModal, setInfoModal] = useState<null | 'pending' | 'hoa'>(null);
  
  // Commute state
  const [commuteAddressRent, setCommuteAddressRent] = useState(() => loadStateFromStorage('commuteAddressRent', ''));
  const [commuteDriveTimeRent, setCommuteDriveTimeRent] = useState<number | null>(() => loadStateFromStorage('commuteDriveTimeRent', null));
  const [commuteAddressTouched, setCommuteAddressTouched] = useState(() => loadStateFromStorage('commuteAddressTouched', false));
  const [commuteSuggestions, setCommuteSuggestions] = useState<any[]>([]);
  const [searchLocationCoords, setSearchLocationCoords] = useState<{ lat: number; lng: number } | null>(() => loadStateFromStorage('searchLocationCoords', null));
  const [commuteCoords, setCommuteCoords] = useState<{ lat: number; lng: number } | null>(() => loadStateFromStorage('commuteCoords', null));
  const [driveTimes, setDriveTimes] = useState<number[] | null>(null);
  const [driveTimesUpdateTrigger, setDriveTimesUpdateTrigger] = useState(0);
  const [isCalculatingDriveTimes, setIsCalculatingDriveTimes] = useState(false);

  // Save state changes to localStorage
  React.useEffect(() => {
    saveStateToStorage('searchQuery', searchQuery);
  }, [searchQuery]);

  React.useEffect(() => {
    saveStateToStorage('debouncedSearchQuery', debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  React.useEffect(() => {
    saveStateToStorage('searchCity', searchCity);
  }, [searchCity]);

  React.useEffect(() => {
    saveStateToStorage('searchState', searchState);
  }, [searchState]);

  React.useEffect(() => {
    saveStateToStorage('activeFilterTab', activeFilterTab);
  }, [activeFilterTab]);

  React.useEffect(() => {

    saveStateToStorage('listings', listings);

  }, [listings]);

  React.useEffect(() => {
    saveStateToStorage('filters', filters);
  }, [filters]);

  React.useEffect(() => {
    saveStateToStorage('selectedPropertyTypesBuySold', selectedPropertyTypesBuySold);
  }, [selectedPropertyTypesBuySold]);

  React.useEffect(() => {
    saveStateToStorage('selectedPropertyTypesRent', selectedPropertyTypesRent);
  }, [selectedPropertyTypesRent]);

  React.useEffect(() => {
    saveStateToStorage('propertyTypePillTouchedRent', propertyTypePillTouchedRent);
  }, [propertyTypePillTouchedRent]);

  React.useEffect(() => {
    saveStateToStorage('propertyTypePillTouchedBuySold', propertyTypePillTouchedBuySold);
  }, [propertyTypePillTouchedBuySold]);

  React.useEffect(() => {
    saveStateToStorage('priceRangeBuySold', priceRangeBuySold);
  }, [priceRangeBuySold]);

  React.useEffect(() => {
    saveStateToStorage('priceRangeRent', priceRangeRent);
  }, [priceRangeRent]);

  

  React.useEffect(() => {
    saveStateToStorage('bedsBuySold', bedsBuySold);
  }, [bedsBuySold]);

  React.useEffect(() => {
    saveStateToStorage('bedsRent', bedsRent);
  }, [bedsRent]);

  React.useEffect(() => {
    saveStateToStorage('bathsBuySold', bathsBuySold);
  }, [bathsBuySold]);

  React.useEffect(() => {
    saveStateToStorage('bathsRent', bathsRent);
  }, [bathsRent]);

  React.useEffect(() => {
    saveStateToStorage('buySearchAreaMiles', buySearchAreaMiles);
  }, [buySearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('rentSearchAreaMiles', rentSearchAreaMiles);
  }, [rentSearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('soldSearchAreaMiles', soldSearchAreaMiles);
  }, [soldSearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('pendingBuySearchAreaMiles', pendingBuySearchAreaMiles);
  }, [pendingBuySearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('pendingRentSearchAreaMiles', pendingRentSearchAreaMiles);
  }, [pendingRentSearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('pendingSoldSearchAreaMiles', pendingSoldSearchAreaMiles);
  }, [pendingSoldSearchAreaMiles]);

  React.useEffect(() => {
    saveStateToStorage('searchAreaPillTouchedBuy', searchAreaPillTouchedBuy);
  }, [searchAreaPillTouchedBuy]);

  React.useEffect(() => {
    saveStateToStorage('searchAreaPillTouchedRent', searchAreaPillTouchedRent);
  }, [searchAreaPillTouchedRent]);

  React.useEffect(() => {
    saveStateToStorage('searchAreaPillTouchedSold', searchAreaPillTouchedSold);
  }, [searchAreaPillTouchedSold]);

  React.useEffect(() => {
    saveStateToStorage('minHomeAge', minHomeAge);
  }, [minHomeAge]);

  React.useEffect(() => {
    saveStateToStorage('maxHomeAge', maxHomeAge);
  }, [maxHomeAge]);

  React.useEffect(() => {
    saveStateToStorage('minSquareFeet', minSquareFeet);
  }, [minSquareFeet]);

  React.useEffect(() => {
    saveStateToStorage('maxSquareFeet', maxSquareFeet);
  }, [maxSquareFeet]);

  React.useEffect(() => {
    saveStateToStorage('pendingMinSquareFeet', pendingMinSquareFeet);
  }, [pendingMinSquareFeet]);

  React.useEffect(() => {
    saveStateToStorage('pendingMaxSquareFeet', pendingMaxSquareFeet);
  }, [pendingMaxSquareFeet]);

  React.useEffect(() => {
    saveStateToStorage('minLotSize', minLotSize);
  }, [minLotSize]);

  React.useEffect(() => {
    saveStateToStorage('maxLotSize', maxLotSize);
  }, [maxLotSize]);

  React.useEffect(() => {
    saveStateToStorage('maxHoaFees', maxHoaFees);
  }, [maxHoaFees]);

  React.useEffect(() => {
    saveStateToStorage('noHoa', noHoa);
  }, [noHoa]);

  React.useEffect(() => {
    saveStateToStorage('pendingDaysOnMarket', pendingDaysOnMarket);
  }, [pendingDaysOnMarket]);

  // Amenities-related useEffect hooks removed

  React.useEffect(() => {
    saveStateToStorage('commuteAddressRent', commuteAddressRent);
  }, [commuteAddressRent]);

  React.useEffect(() => {
    saveStateToStorage('commuteDriveTimeRent', commuteDriveTimeRent);
  }, [commuteDriveTimeRent]);

  React.useEffect(() => {
    saveStateToStorage('commuteAddressTouched', commuteAddressTouched);
  }, [commuteAddressTouched]);

  React.useEffect(() => {
    saveStateToStorage('searchLocationCoords', searchLocationCoords);
  }, [searchLocationCoords]);

  React.useEffect(() => {
    saveStateToStorage('commuteCoords', commuteCoords);
  }, [commuteCoords]);
  
  // Helper function to handle setDriveTimesUpdateTrigger with function pattern
  const setDriveTimesUpdateTriggerWithFunction = (trigger: number | ((prev: number) => number)) => {
    if (typeof trigger === 'function') {
      setDriveTimesUpdateTrigger(trigger);
    } else {
      setDriveTimesUpdateTrigger(trigger);
    }
  };
  
  // Helper functions
  const getSelectedPropertyTypes = () =>
    activeFilterTab === 'rent' ? selectedPropertyTypesRent : selectedPropertyTypesBuySold;
  
  const setSelectedPropertyTypesForTab = (types: string[]) => {
    if (activeFilterTab === 'rent') {
      setSelectedPropertyTypesRent(types);
    } else {
      setSelectedPropertyTypesBuySold(types);
    }
  };

  const getCurrentPriceRange = () =>
    activeFilterTab === 'rent' ? priceRangeRent : priceRangeBuySold;
  
  const setCurrentPriceRange = (range: [number | null, number | null]) => {
    if (activeFilterTab === 'rent') {
      setPriceRangeRent(range);
    } else {
      setPriceRangeBuySold(range);
    }
  };

  const getCurrentBeds = () => activeFilterTab === 'rent' ? bedsRent : bedsBuySold;
  const setCurrentBeds = (val: number | null) => {
    if (activeFilterTab === 'rent') setBedsRent(val);
    else setBedsBuySold(val);
  };
  
  const getCurrentBaths = () => activeFilterTab === 'rent' ? bathsRent : bathsBuySold;
  const setCurrentBaths = (val: number | null) => {
    if (activeFilterTab === 'rent') setBathsRent(val);
    else setBathsBuySold(val);
  };

  const getPropertyTypePillTouched = () => 
    activeFilterTab === 'rent' ? propertyTypePillTouchedRent : propertyTypePillTouchedBuySold;
  
  const setPropertyTypePillTouchedForTab = (value: boolean) => {
    if (activeFilterTab === 'rent') {
      setPropertyTypePillTouchedRent(value);
    } else {
      setPropertyTypePillTouchedBuySold(value);
    }
  };

  const getCurrentSearchAreaMiles = () => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      return buySearchAreaMiles; // Both buy and sold use the same value
    } else if (activeFilterTab === 'rent') {
      return rentSearchAreaMiles;
    }
    return 0;
  };
  
  const setCurrentSearchAreaMiles = (value: number) => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      setBuySearchAreaMiles(value);
      setSoldSearchAreaMiles(value);
      setSearchAreaPillTouchedBuy(value > 0);
      setSearchAreaPillTouchedSold(value > 0);
    } else if (activeFilterTab === 'rent') {
      setRentSearchAreaMiles(value);
      setSearchAreaPillTouchedRent(value > 0);
    }
  };

  const getPendingSearchAreaMiles = () => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      return pendingBuySearchAreaMiles; // Both buy and sold use the same value
    } else if (activeFilterTab === 'rent') {
      return pendingRentSearchAreaMiles;
    }
    return null;
  };
  
  const setPendingSearchAreaMilesForTab = (value: number | null) => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      setPendingBuySearchAreaMiles(value);
      setPendingSoldSearchAreaMiles(value);
    } else if (activeFilterTab === 'rent') {
      setPendingRentSearchAreaMiles(value);
    }
  };

  const getSearchAreaPillTouched = () => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      return searchAreaPillTouchedBuy; // Both buy and sold use the same value
    } else if (activeFilterTab === 'rent') {
      return searchAreaPillTouchedRent;
    }
    return false;
  };
  
  const setSearchAreaPillTouchedForTab = (value: boolean) => {
    if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
      setSearchAreaPillTouchedBuy(value);
      setSearchAreaPillTouchedSold(value);
    } else if (activeFilterTab === 'rent') {
      setSearchAreaPillTouchedRent(value);
    }
  };
  
  // Reset functions
  const resetBuySoldFilters = () => {
    setBuySearchAreaMiles(0);
    setSoldSearchAreaMiles(0);
    setPendingBuySearchAreaMiles(null);
    setPendingSoldSearchAreaMiles(null);
    setPropertyTypePillTouchedBuySold(false);
    setSelectedPropertyTypesBuySold([...buyPropertyTypes]);
    setPriceRangeBuySold([null, null]);
    setBedsBuySold(null);
    setBathsBuySold(null);
    // Amenities reset removed
    setFilters(prev => ({ 
      ...prev, 
      propertyType: null, 
      hidePendingContingent: false,
      newConstructionOnly: false
    }));
    
    // Clear search key when filters are reset to ensure fresh search
    try {
      localStorage.removeItem('homeFilters_lastSearchKey');
    } catch (error) {
      // localStorage access failed, continue
    }
  };

  const resetRentFilters = () => {
    setRentSearchAreaMiles(0);
    setPendingRentSearchAreaMiles(null);
    setPropertyTypePillTouchedRent(false);
    setSelectedPropertyTypesRent([...rentPropertyTypes]);
    setPriceRangeRent([null, null]);
    setBedsRent(null);
    setBathsRent(null);
    // Amenities reset removed
    setFilters(prev => ({ 
      ...prev, 
      propertyType: null, 
      hidePendingContingent: false,
      newConstructionOnly: false
    }));
    
    // Clear search key when filters are reset to ensure fresh search
    try {
      localStorage.removeItem('homeFilters_lastSearchKey');
    } catch (error) {
      // localStorage access failed, continue
    }
  };

  const clearFilters = () => {
    setFilters({
      priceRange: [null, null],
      beds: null,
      baths: null,
      propertyType: null,
      hidePendingContingent: false,
      newConstructionOnly: false,
      hideHomesNotYetBuilt: false,
      daysOnMarket: null,
    });
    setMinHomeAge('');
    setMaxHomeAge('');
    setMaxHoaFees('');
    setNoHoa(false);
    setPendingDaysOnMarket(null);
    // Amenities reset removed
    
    // Clear search key when filters are cleared to ensure fresh search
    try {
      localStorage.removeItem('homeFilters_lastSearchKey');
    } catch (error) {
      // localStorage access failed, continue
    }
  };
  
  // Filter change handler
  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Property type handlers
  const togglePropertyType = (type: string) => {
    const currentTypes = getSelectedPropertyTypes();
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t: string) => t !== type)
      : [...currentTypes, type];
    setSelectedPropertyTypesForTab(newTypes);
    // Mark the pill as touched when user makes any selection
    setPropertyTypePillTouchedForTab(true);
  };

  const toggleFilterOption = (key: keyof Filters) => {
    setFilters(prev => {
      const newValue = !prev[key];
      return { ...prev, [key]: newValue };
    });
  };

  // Refresh property types from cache
  const refreshPropertyTypes = () => {
    const newRentTypes = [...rentPropertyTypes];
    const newBuyTypes = [...buyPropertyTypes];
    
    // Update selected types to include any new types that weren't previously selected
    setSelectedPropertyTypesRent((prev: string[]) => {
      const newTypes = newRentTypes.filter(type => !prev.includes(type));
      return [...prev, ...newTypes];
    });
    
    setSelectedPropertyTypesBuySold((prev: string[]) => {
      const newTypes = newBuyTypes.filter(type => !prev.includes(type));
      return [...prev, ...newTypes];
    });
  };

  // Replace getFilteredListingsCount with real filter logic
  const getFilteredListingsCount = () => {
    let properties = listings;
    const [min, max] = getCurrentPriceRange();
    const beds = getCurrentBeds();
    const baths = getCurrentBaths();
    const searchAreaMiles = getCurrentSearchAreaMiles();
    // Search area filter (by miles from geocoded search location)
    if (searchAreaMiles > 0 && searchLocationCoords) {
      properties = properties.filter(property => {
        const lat = property.location?.lat;
        const lon = property.location?.lon;
        if (typeof lat !== 'number' || typeof lon !== 'number') return false;
        const dist = typeof haversineDistance === 'function'
          ? haversineDistance(searchLocationCoords.lat, searchLocationCoords.lng, lat, lon)
          : 0;
        return dist <= searchAreaMiles;
      });
    }
    const filtered = properties.filter(property => {
      // Price range filter - handle both single prices and price ranges
      const propertyMinPrice = (property as any).rentalMetadata?.minPrice || property.price;
      const propertyMaxPrice = (property as any).rentalMetadata?.maxPrice || property.price;
      
      // Check if the user's price range overlaps with the property's price range
      if (min !== null && propertyMaxPrice < min) return false; // Property's max price is below user's minimum
      if (max !== null && propertyMinPrice > max) return false; // Property's min price is above user's maximum
      if (beds !== null && (property.details?.bedrooms === undefined || property.details?.bedrooms < beds)) return false;
      if (baths !== null && (property.details?.bathrooms === undefined || property.details?.bathrooms < baths)) return false;
      if (filters.propertyType && property.type !== filters.propertyType) return false;
      const selectedPropertyTypes = getSelectedPropertyTypes();
      const propertyTypePillTouched = getPropertyTypePillTouched();
      if (propertyTypePillTouched && selectedPropertyTypes.length > 0 && property.type && !isPropertyTypeMatch(property.type, selectedPropertyTypes, activeFilterTab)) return false;
      if (minSquareFeet && property.details?.squareFeet && property.details.squareFeet < parseInt(minSquareFeet)) return false;
      if (maxSquareFeet && property.details?.squareFeet && property.details.squareFeet > parseInt(maxSquareFeet)) return false;
      const lotSizeRaw = property.details?.lotSize;
      const lotSizeAmount = lotSizeRaw && lotSizeRaw.amount ? parseInt(lotSizeRaw.amount) : null;
      if (minLotSize && lotSizeAmount !== null && lotSizeAmount < parseInt(minLotSize)) return false;
      if (maxLotSize && lotSizeAmount !== null && lotSizeAmount > parseInt(maxLotSize)) return false;
      if (commuteAddressRent && commuteDriveTimeRent !== null && commuteDriveTimeRent !== undefined) {
        let propertyDriveTime = null;
        if (driveTimes && driveTimes.length > 0) {
          if (property.location && property.location.lat && property.location.lon) {
            const originalIndex = listings.findIndex(l => l.location && l.location.lat === property.location.lat && l.location.lon === property.location.lon);
            if (originalIndex >= 0 && originalIndex < driveTimes.length) {
              propertyDriveTime = driveTimes[originalIndex];
            }
          }
          if (propertyDriveTime === null) {
            const originalIndex = listings.findIndex(l => l.id === property.id);
            if (originalIndex >= 0 && originalIndex < driveTimes.length) {
              propertyDriveTime = driveTimes[originalIndex];
            }
          }
        }
        
        if (propertyDriveTime === null || propertyDriveTime > commuteDriveTimeRent) {
          return false;
        }
      }
      if (filters.hidePendingContingent && property.status && (property.status === 'pending' || property.status === 'contingent')) return false;
      if ((minHomeAge || maxHomeAge) && property.details?.yearBuilt) {
        const currentYear = new Date().getFullYear();
        const age = currentYear - property.details.yearBuilt;
        if (minHomeAge && age < (typeof minHomeAge === 'number' ? minHomeAge : Number(minHomeAge) || 0)) return false;
        if (maxHomeAge && age > (typeof maxHomeAge === 'number' ? maxHomeAge : Number(maxHomeAge) || 0)) return false;
      }
      // New construction filter
      if (filters.newConstructionOnly && property.details?.yearBuilt) {
        const currentYear = new Date().getFullYear();
        const age = currentYear - property.details.yearBuilt;
        // New construction = built in last 1-2 years
        if (age > 2) return false;
      }
      // HOA filter
      const hoaAmount = property.hoa_dues && property.hoa_dues.amount ? (typeof property.hoa_dues.amount === 'number' ? property.hoa_dues.amount : Number(property.hoa_dues.amount) || 0) : 0;
      if (noHoa && hoaAmount > 0) return false;
      // Only exclude properties with HOA fees greater than maxHoaFees
      if (!noHoa && maxHoaFees.trim() !== '' && hoaAmount > (typeof maxHoaFees === 'number' ? maxHoaFees : Number(maxHoaFees) || 0)) return false;
      // Days on market filter
      if (filters.daysOnMarket !== null && filters.daysOnMarket !== undefined) {
        // If 12 months+ (365+ days) is selected, show all properties regardless of days on market
        if (filters.daysOnMarket >= 365) {
          // Skip days on market filtering - show all properties
        } else {
          // Apply normal days on market filtering for filters less than 12 months
          // Try to get days on market from various sources in order of preference
          let propertyDaysOnMarket = (property as any).daysOnMarket;
          let calculationSource = 'daysOnMarket field';
          
          // If daysOnMarket is 0 or undefined, try other sources
          if (!propertyDaysOnMarket || propertyDaysOnMarket === 0) {
            // Try daysOnZillow first (primary source)
            if (property.daysOnZillow && property.daysOnZillow > 0) {
              propertyDaysOnMarket = property.daysOnZillow;
              calculationSource = 'daysOnZillow field';
            }
            // Try timeOnZillow (already in days for sale properties)
            else if ((property as any).timeOnZillow && (property as any).timeOnZillow > 0) {
              propertyDaysOnMarket = (property as any).timeOnZillow;
              calculationSource = 'timeOnZillow field';
            }
            // Try days_on_market
            else if ((property as any).days_on_market && (property as any).days_on_market > 0) {
              propertyDaysOnMarket = (property as any).days_on_market;
              calculationSource = 'days_on_market field';
            }
            // Try time_on_zillow (already in days)
            else if ((property as any).time_on_zillow && (property as any).time_on_zillow > 0) {
              propertyDaysOnMarket = (property as any).time_on_zillow;
              calculationSource = 'time_on_zillow field';
            }
            // TIMESTAMP CONVERSION FALLBACK - only for properties with no days data
            // Try rentalMetadata.listingDateTimeOnZillow (for rental properties)
            else if ((property as any).rentalMetadata?.listingDateTimeOnZillow && typeof (property as any).rentalMetadata.listingDateTimeOnZillow === 'number') {
              const listingDate = new Date((property as any).rentalMetadata.listingDateTimeOnZillow);
              const now = new Date();
              const diffTime = Math.abs(now.getTime() - listingDate.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              propertyDaysOnMarket = diffDays;
              calculationSource = 'rentalMetadata timestamp calculation';
            }
            // Try direct listingDateTimeOnZillow (fallback)
            else if ((property as any).listingDateTimeOnZillow && typeof (property as any).listingDateTimeOnZillow === 'number') {
              const listingDate = new Date((property as any).listingDateTimeOnZillow);
              const now = new Date();
              const diffTime = Math.abs(now.getTime() - listingDate.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              propertyDaysOnMarket = diffDays;
              calculationSource = 'listingDateTimeOnZillow timestamp calculation';
            }
            else {
              calculationSource = 'no data available';
            }
          }
          
          // Filter out properties with 0 days on market (inaccurate data) or properties exceeding the filter
          if (propertyDaysOnMarket !== undefined && (propertyDaysOnMarket === 0 || propertyDaysOnMarket >= filters.daysOnMarket)) {
            return false;
          }
        }
      }
      
      
      // Amenities filter removed - data was unreliable
      
      return true;
    });
    return filtered.length;
  };


  // Using OpenStreetMap instead of Google Maps

  const value: HomeFiltersContextType = {
    // Search state
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    setDebouncedSearchQuery,
    isSearching,
    setIsSearching,
    listings,
    setListings,
    searchCity,
    setSearchCity,
    searchState,
    setSearchState,
    
    // Filter state
    showFilters,
    setShowFilters,
    activeFilterTab,
    setActiveFilterTab,
    handleSetActiveFilterTab,
    filters,
    setFilters,
    
    // Property type state
    selectedPropertyTypesBuySold,
    setSelectedPropertyTypesBuySold,
    selectedPropertyTypesRent,
    setSelectedPropertyTypesRent,
    propertyTypePillTouchedRent,
    setPropertyTypePillTouchedRent,
    propertyTypePillTouchedBuySold,
    setPropertyTypePillTouchedBuySold,
    showPropertyTypeModal,
    setShowPropertyTypeModal,
    propertyTypeWarning,
    setPropertyTypeWarning,
    
    // Price state
    priceRangeBuySold,
    setPriceRangeBuySold,
    priceRangeRent,
    setPriceRangeRent,
    
    // Beds and baths state
    bedsBuySold,
    setBedsBuySold,
    bedsRent,
    setBedsRent,
    bathsBuySold,
    setBathsBuySold,
    bathsRent,
    setBathsRent,
    
    // Search area state
    buySearchAreaMiles,
    setBuySearchAreaMiles,
    rentSearchAreaMiles,
    setRentSearchAreaMiles,
    soldSearchAreaMiles,
    setSoldSearchAreaMiles,
    showSearchAreaDropdown,
    setShowSearchAreaDropdown,
    pendingBuySearchAreaMiles,
    setPendingBuySearchAreaMiles,
    pendingRentSearchAreaMiles,
    setPendingRentSearchAreaMiles,
    pendingSoldSearchAreaMiles,
    setPendingSoldSearchAreaMiles,
    searchAreaPillTouchedBuy,
    setSearchAreaPillTouchedBuy,
    searchAreaPillTouchedRent,
    setSearchAreaPillTouchedRent,
    searchAreaPillTouchedSold,
    setSearchAreaPillTouchedSold,
    
    // Modal state
    // Amenities modal removed
    showSquareFeetModal,
    setShowSquareFeetModal,
    showLotSizeModal,
    setShowLotSizeModal,
    showLotSizeDropdown,
    setShowLotSizeDropdown,
    showHomeAgeDropdown,
    setShowHomeAgeDropdown,
    showDaysOnMarketModal,
    setShowDaysOnMarketModal,
    showSquareFeetDropdown,
    setShowSquareFeetDropdown,
    
    // Filter values
    minHomeAge,
    setMinHomeAge,
    maxHomeAge,
    setMaxHomeAge,
    minSquareFeet,
    setMinSquareFeet,
    maxSquareFeet,
    setMaxSquareFeet,
    pendingMinSquareFeet,
    setPendingMinSquareFeet,
    pendingMaxSquareFeet,
    setPendingMaxSquareFeet,
    minLotSize,
    setMinLotSize,
    maxLotSize,
    setMaxLotSize,
    maxHoaFees,
    setMaxHoaFees,
    noHoa,
    setNoHoa,
    pendingDaysOnMarket,
    setPendingDaysOnMarket,
    // Amenities-related properties removed
    
    // Animation state
    resetBouncing,
    setResetBouncing,
    daysOnMarketResetBouncing,
    setDaysOnMarketResetBouncing,
    // Amenities animation state removed
    
    // Info modal state
    infoModal,
    setInfoModal,
    
    // Commute state
    commuteAddressRent,
    setCommuteAddressRent,
    commuteDriveTimeRent,
    setCommuteDriveTimeRent,
    commuteAddressTouched,
    setCommuteAddressTouched,
    commuteSuggestions,
    setCommuteSuggestions,
    searchLocationCoords,
    setSearchLocationCoords,
    commuteCoords,
    setCommuteCoords,
    driveTimes,
    setDriveTimes,
    driveTimesUpdateTrigger,
    setDriveTimesUpdateTrigger: setDriveTimesUpdateTriggerWithFunction,
    isCalculatingDriveTimes,
    setIsCalculatingDriveTimes,
    
    // Helper functions
    getSelectedPropertyTypes,
    setSelectedPropertyTypesForTab,
    getCurrentPriceRange,
    setCurrentPriceRange,
    getCurrentBeds,
    setCurrentBeds,
    getCurrentBaths,
    setCurrentBaths,
    getPropertyTypePillTouched,
    setPropertyTypePillTouchedForTab,
    getCurrentSearchAreaMiles,
    setCurrentSearchAreaMiles,
    getPendingSearchAreaMiles,
    setPendingSearchAreaMilesForTab,
    getSearchAreaPillTouched,
    setSearchAreaPillTouchedForTab,
    
    // Reset functions
    resetBuySoldFilters,
    resetRentFilters,
    clearFilters,
    
    // Filter change handler
    handleFilterChange,
    
    // Property type handlers
    togglePropertyType,
    toggleFilterOption,
    getFilteredListingsCount,
    refreshPropertyTypes,
    // Removed Google Maps - now using OpenStreetMap
  };

  return (
    <HomeFiltersContext.Provider value={value}>
      {children}
    </HomeFiltersContext.Provider>
  );
}

export function useHomeFilters() {
  const context = useContext(HomeFiltersContext);
  if (context === undefined) {
    throw new Error('useHomeFilters must be used within a HomeFiltersProvider');
  }
  return context;
} 